import React, { useEffect, useState } from 'react';
import {
  RiCloseLine,
  RiInformationLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiAlertLine
} from 'react-icons/ri';
import { NotificationMessage } from '../../hooks/useWebSocket';

export interface NotificationToastProps {
  notification: NotificationMessage;
  onClose: () => void;
  duration?: number; // ระยะเวลาในการแสดง (milliseconds)
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  duration = 5000,
  position = 'top-right'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // แสดง toast เมื่อ component mount
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(showTimer);
  }, []);

  // ปิด toast อัตโนมัติหลังจากเวลาที่กำหนด
  useEffect(() => {
    if (duration > 0) {
      const hideTimer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(hideTimer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // รอให้ animation เสร็จ
  };

  // ฟังก์ชันสำหรับแสดงไอคอนตามประเภทการแจ้งเตือน
  const getNotificationIcon = () => {
    switch (notification.type) {
      case 'success':
        return <RiCheckboxCircleLine className="w-6 h-6 text-success-500" />;
      case 'warning':
        return <RiAlertLine className="w-6 h-6 text-warning-500" />;
      case 'error':
        return <RiErrorWarningLine className="w-6 h-6 text-danger-500" />;
      default:
        return <RiInformationLine className="w-6 h-6 text-info-500" />;
    }
  };

  // ฟังก์ชันสำหรับแสดงสีและสไตล์ตามประเภท
  const getNotificationStyles = () => {
    switch (notification.type) {
      case 'success':
        return {
          container: 'bg-white border-l-4 border-success-500 shadow-lg',
          title: 'text-success-800',
          message: 'text-success-700',
          progress: 'bg-success-500'
        };
      case 'warning':
        return {
          container: 'bg-white border-l-4 border-warning-500 shadow-lg',
          title: 'text-warning-800',
          message: 'text-warning-700',
          progress: 'bg-warning-500'
        };
      case 'error':
        return {
          container: 'bg-white border-l-4 border-danger-500 shadow-lg',
          title: 'text-danger-800',
          message: 'text-danger-700',
          progress: 'bg-danger-500'
        };
      default:
        return {
          container: 'bg-white border-l-4 border-info-500 shadow-lg',
          title: 'text-info-800',
          message: 'text-info-700',
          progress: 'bg-info-500'
        };
    }
  };

  // ฟังก์ชันสำหรับตำแหน่งของ toast
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      default:
        return 'top-4 right-4';
    }
  };

  // ฟังก์ชันสำหรับ animation classes
  const getAnimationClasses = () => {
    if (isExiting) {
      return 'opacity-0 transform translate-x-full';
    }
    if (isVisible) {
      return 'opacity-100 transform translate-x-0';
    }
    return 'opacity-0 transform translate-x-full';
  };

  const styles = getNotificationStyles();

  return (
    <div
      className={`fixed z-50 w-96 max-w-sm transition-all duration-300 ease-in-out ${getPositionClasses()} ${getAnimationClasses()}`}
    >
      <div className={`${styles.container} rounded-lg p-4 relative overflow-hidden`}>
        {/* Progress Bar */}
        {duration > 0 && (
          <div
            className={`absolute top-0 left-0 h-1 ${styles.progress} transition-all ease-linear`}
            style={{
              width: isVisible ? '0%' : '100%',
              transitionDuration: `${duration}ms`
            }}
          />
        )}

        <div className="flex items-start space-x-3">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            {getNotificationIcon()}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${styles.title} mb-1`}>
                  {notification.title}
                </h4>
                <p className={`text-sm ${styles.message}`}>
                  {notification.message}
                </p>
                
                {/* Category Badge */}
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700">
                    {getCategoryLabel(notification.category)}
                  </span>
                </div>

                {/* Additional Data */}
                {notification.data && (
                  <div className="mt-2 text-xs text-secondary-600">
                    {typeof notification.data === 'object' ? (
                      <pre className="whitespace-pre-wrap [overflow-wrap:anywhere] font-mono bg-secondary-100 p-2 rounded text-xs">
                        {JSON.stringify(notification.data, null, 2)}
                      </pre>
                    ) : (
                      <p>{String(notification.data)}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Close Button */}
              <button
                onClick={handleClose}
                className="flex-shrink-0 text-secondary-400 hover:text-secondary-600 transition-colors ml-2"
                aria-label="ปิดการแจ้งเตือน"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Click to dismiss */}
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="คลิกเพื่อปิดการแจ้งเตือน"
        />
      </div>
    </div>
  );
};

// ฟังก์ชันสำหรับแปลงประเภทการแจ้งเตือนเป็นภาษาไทย
const getCategoryLabel = (category: NotificationMessage['category']) => {
  switch (category) {
    case 'rental':
      return 'การเช่า';
    case 'contract':
      return 'สัญญา';
    case 'payment':
      return 'การชำระเงิน';
    case 'maintenance':
      return 'การบำรุงรักษา';
    case 'system':
    default:
      return 'ระบบ';
  }
};

export default NotificationToast;